import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

// Görsel geçiş animasyonu için framer-motion varyantları
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

// Sayfa düzeni bileşenleri
const PressSection = styled.section`
  padding: 120px 0 80px;
  background: linear-gradient(180deg, #0D0D0D 0%, #1A1A1A 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 70% 30%, rgba(226, 149, 120, 0.05), transparent 60%);
    pointer-events: none;
  }
`;

const PressContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 1;
`;

const PageHeading = styled.div`
  margin-bottom: 80px;
  position: relative;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 300;
  margin-bottom: 1.5rem;
  color: #ffffff;
  letter-spacing: 0.05em;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: #e29578;
  }
  
  @media (max-width: 768px) {
  font-size: 2.5rem;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 700px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;


// Press grid and cards
const MediaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 80px;
  
  @media (max-width: 1200px) {
    gap: 25px;
  }
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const NewsCard = styled(motion.a)`
  background-color: #1E1E1E;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  flex: 1 1 calc(33.333% - 30px);
  min-width: 350px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(226, 149, 120, 0.3);
  }
  
  @media (max-width: 1200px) {
    flex: 1 1 calc(50% - 25px);
    min-width: 300px;
  }
  
  @media (max-width: 768px) {
    flex: 1 1 calc(50% - 20px);
    min-width: 250px;
  }
  
  @media (max-width: 480px) {
    flex: 1 1 100%;
    min-width: 200px;
  }
`;


const CardImageContainer = styled.div`
  width: 100%;
  background-color: #151515;
  position: relative;
  overflow: hidden;
`;

const WideImageContainer = styled(CardImageContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MediumImageContainer = styled(CardImageContainer)`
  aspect-ratio: 4 / 3;
`;

const NarrowImageContainer = styled(CardImageContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  object-position: center;
`;

const CardContent = styled.div`
  padding: 24px;
  background-color: #1E1E1E;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 12px;
  color: #ffffff;
  line-height: 1.4;
  transition: color 0.3s ease;
  
  ${NewsCard}:hover & {
    color: #e29578;
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CardDate = styled.time`
  font-size: 0.85rem;
  color: #e29578;
  display: block;
  margin-bottom: 12px;
  font-weight: 500;
`;

const CardSource = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background-color: rgba(226, 149, 120, 0.1);
  border: 1px solid rgba(226, 149, 120, 0.2);
  border-radius: 20px;
  font-size: 0.8rem;
  color: #e29578;
  font-weight: 500;
  margin-bottom: 12px;
`;

const CardDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
  
  @media (max-width: 1024px) {
    font-size: 0.88rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 400;
  color: #ffffff;
  margin-bottom: 40px;
  position: relative;
  padding-left: 20px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 70%;
    background-color: #e29578;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

// Simple Image Slider
const SimpleSlider = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SliderImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  transition: opacity 0.3s ease;
`;

const SliderButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 8px;' : 'right: 8px;'}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s ease;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  outline: none;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: translateY(-50%);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: none;
  }
`;

// Görsel büyütme lightbox
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  cursor: pointer;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: default;
  pointer-events: none;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: white;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ImageClickWrapper = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: block;
`;

// Haber öğesi için TypeScript tipi
interface MediaItem {
  id: string;
  title: string;
  titleEn?: string;
  titleTr?: string;
  titleFr?: string;
  titleEs?: string;
  titleAr?: string;
  image: string;
  images?: string[]; // Birden fazla görsel için
  date?: string;
  source: string;
  category: string;
  url?: string; // İsteğe bağlı URL özelliği
  description?: string; // Açıklama (varsayılan / Türkçe)
  descriptionEn?: string; // İngilizce açıklama
  descriptionTr?: string; // Türkçe açıklama (İngilizce olan kartlar için)
  descriptionFr?: string;
  descriptionEs?: string;
  descriptionAr?: string;
}

// Press component
const Press: React.FC = () => {
  const { language } = useLanguage();
  const [sliderStates, setSliderStates] = useState<{[key: string]: number}>({});
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxImage]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    if (lightboxImage) window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [lightboxImage]);
  
  // Translations for the page title and other text
  const translations = {
    en: {
      pageTitle: 'Press Coverage',
      pageSubtitle: 'Explore our media coverage and press releases from around the world',
    },
    tr: {
      pageTitle: 'Basın',
      pageSubtitle: 'Dünya çapındaki medya haberlerimizi ve basın bültenlerimizi keşfedin',
    },
    fr: {
      pageTitle: 'Presse',
      pageSubtitle: 'Découvrez notre couverture médiatique et nos communiqués de presse du monde entier',
    },
    es: {
      pageTitle: 'Prensa',
      pageSubtitle: 'Explore nuestra cobertura mediática y comunicados de prensa de todo el mundo',
    },
    ar: {
      pageTitle: 'الصحافة',
      pageSubtitle: 'استكشف تغطيتنا الإعلامية وبيانات صحفية من جميع أنحاء العالم',
    }
  };
  
  // Get proper translation based on language
  const getText = (key: string) => {
    return translations[language as keyof typeof translations]?.[key as keyof (typeof translations)['en']] || key;
  };

  // Haber başlığını seçili dile göre döndür
  const getItemTitle = (item: MediaItem): string => {
    if (language === 'en' && item.titleEn) return item.titleEn;
    if (language === 'tr' && item.titleTr) return item.titleTr;
    if (language === 'fr' && item.titleFr) return item.titleFr;
    if (language === 'es' && item.titleEs) return item.titleEs;
    if (language === 'ar' && item.titleAr) return item.titleAr;
    return item.title;
  };

  // Haber açıklamasını seçili dile göre döndür
  const getItemDescription = (item: MediaItem): string => {
    if (language === 'en' && item.descriptionEn) return item.descriptionEn;
    if (language === 'tr' && item.descriptionTr) return item.descriptionTr;
    if (language === 'fr' && item.descriptionFr) return item.descriptionFr;
    if (language === 'es' && item.descriptionEs) return item.descriptionEs;
    if (language === 'ar' && item.descriptionAr) return item.descriptionAr;
    return item.description || '';
  };
  
  // Geniş format haberler (16:9)
  const wideFormatMedia: MediaItem[] = [
    {
      id: 'gazi-meclis-enerji-bakani',
      title: 'Barkın Sayın, Gazi Meclis\'te Enerji Bakanı ile Bir Araya Geldi',
      titleEn: 'Barkın Sayın Meets the Minister of Energy at the Grand National Assembly',
      titleFr: 'Barkın Sayın rencontre le ministre de l\'Énergie à la Grande Assemblée nationale',
      titleEs: 'Barkın Sayın se reúne con el Ministro de Energía en la Gran Asamblea Nacional',
      titleAr: 'باركين صايين يلتقي وزير الطاقة في البرلمان التركي الكبير',
      image: '/images/press/gazi-meclis-enerji-bakani.webp',
      source: 'Ankara / TBMM',
      category: 'events',
      date: '2026-02-18',
      description: 'İş insanı ve BSS Holding Yönetim Kurulu Başkanı Barkın Sayın, Türkiye Büyük Millet Meclisi\'nde Enerji Bakanı ile tanışma fırsatı buldu. Görüşmenin sıcak ve samimi bir atmosferde geçtiği belirtildi. Barkın Sayın, Bakan\'ın kendisine "kardeşim" şeklindeki hitabının kendisi açısından son derece samimi ve kıymetli olduğunu ifade etti. Buluşma, Gazi Meclis\'te gerçekleşen anlamlı temaslardan biri olarak değerlendirildi.',
      descriptionEn: 'Businessman and BSS Holding Chairman of the Board Barkın Sayın had the opportunity to meet the Minister of Energy at the Grand National Assembly of Turkey. The meeting was said to have taken place in a warm and sincere atmosphere. Barkın Sayın stated that the Minister addressing him as "my brother" was extremely sincere and valuable to him. The meeting was regarded as one of the meaningful contacts held at the Grand National Assembly.',
      descriptionFr: 'L\'homme d\'affaires et président du conseil d\'administration de BSS Holding, Barkın Sayın, a eu l\'occasion de rencontrer le ministre de l\'Énergie à la Grande Assemblée nationale de Turquie. La réunion se serait déroulée dans une atmosphère chaleureuse et sincère. Barkın Sayın a déclaré que le fait que le ministre s\'adresse à lui en l\'appelant « mon frère » était extrêmement sincère et précieux pour lui. La rencontre a été considérée comme l\'un des contacts significatifs tenus à la Grande Assemblée nationale.',
      descriptionEs: 'El empresario y presidente del Consejo de Administración de BSS Holding, Barkın Sayın, tuvo la oportunidad de reunirse con el Ministro de Energía en la Gran Asamblea Nacional de Turquía. Se indicó que la reunión se desarrolló en un ambiente cálido y sincero. Barkın Sayın expresó que el hecho de que el Ministro se dirigiera a él como "mi hermano" fue para él sumamente sincero y valioso. El encuentro fue valorado como uno de los contactos significativos celebrados en la Gran Asamblea Nacional.',
      descriptionAr: 'أتيحت لرجل الأعمال ورئيس مجلس إدارة BSS Holding باركين صايين فرصة لقاء وزير الطاقة في البرلمان التركي الكبير. وذُكر أن اللقاء جرى في أجواء دافئة وصادقة. وأعرب باركين صايين عن أن مخاطبة الوزير له بـ"أخي" كانت بالنسبة له صادقة وقيّمة للغاية. واعتُبر اللقاء من بين الاتصالات المهمة التي جرت في البرلمان التركي الكبير.'
    },
    {
      id: 'gazi-meclis-dostlar',
      title: 'Barkın Sayın, Gazi Meclis\'te Dostlarıyla Bir Araya Geldi',
      titleEn: 'Barkın Sayın Comes Together with His Friends at the Grand National Assembly',
      titleFr: 'Barkın Sayın retrouve ses amis à la Grande Assemblée nationale',
      titleEs: 'Barkın Sayın se reúne con sus amigos en la Gran Asamblea Nacional',
      titleAr: 'باركين صايين يجتمع مع أصدقائه في البرلمان التركي الكبير',
      image: '/images/press/gazi-meclis-dostlar.webp',
      source: 'Ankara / TBMM',
      category: 'events',
      date: '2026-02-18',
      description: 'İş insanı Barkın Sayın, Ankara ziyareti kapsamında Türkiye Büyük Millet Meclisi\'nde dostlarıyla bir araya geldi. Gerçekleşen görüşmelerde güncel gelişmeler ve iş dünyasına ilişkin değerlendirmelerde bulunuldu. Sayın, nazik ev sahiplikleri ve samimi sohbetleri için görüştüğü dostlarına teşekkür etti.',
      descriptionEn: 'Businessman Barkın Sayın came together with his friends at the Grand National Assembly of Turkey during his visit to Ankara. During the meetings, current developments and assessments regarding the business world were discussed. Sayın thanked the friends he met for their kind hospitality and sincere conversations.',
      descriptionFr: 'L\'homme d\'affaires Barkın Sayın a retrouvé ses amis à la Grande Assemblée nationale de Turquie lors de sa visite à Ankara. Au cours des entretiens, les développements actuels et les évaluations concernant le monde des affaires ont été abordés. Sayın a remercié les amis qu\'il a rencontrés pour leur aimable hospitalité et leurs conversations sincères.',
      descriptionEs: 'El empresario Barkın Sayın se reunió con sus amigos en la Gran Asamblea Nacional de Turquía durante su visita a Ankara. En las reuniones se trataron los desarrollos actuales y las evaluaciones relativas al mundo empresarial. Sayın agradeció a los amigos con los que se reunió por su amable hospitalidad y sus sinceras conversaciones.',
      descriptionAr: 'اجتمع رجل الأعمال باركين صايين مع أصدقائه في البرلمان التركي الكبير خلال زيارته لأنقرة. وخلال اللقاءات، جرى تناول التطورات الراهنة والتقييمات المتعلقة بعالم الأعمال. وشكر صايين الأصدقاء الذين التقى بهم على حسن ضيافتهم وأحاديثهم الصادقة.'
    },
    {
      id: 'taba-tbmm-barkin-serkan-bayram',
      title: 'Prof. Dr. Halit Yerebakan ve Barkın Sayın Ankara\'daki İftar Programında Buluştu',
      titleEn: 'Prof. Dr. Halit Yerebakan and Barkın Sayın Meet at Iftar Program in Ankara',
      titleFr: 'Le Prof. Dr. Halit Yerebakan et Barkın Sayın se rencontrent au programme d\'iftar à Ankara',
      titleEs: 'El Prof. Dr. Halit Yerebakan y Barkın Sayın se reúnen en el programa de iftar en Ankara',
      titleAr: 'البروفيسور د. خليط يريباكان وباركين صايين يلتقيان في برنامج الإفطار في أنقرة',
      image: '/images/press/taba-tbmm-serkan-bayram.webp',
      source: 'Ankara / TBMM',
      category: 'events',
      date: '2025-03-01',
      description: 'AK Parti Genel Başkan Yardımcısı ve Sağlık Politikaları Başkanı Prof. Dr. Halit Yerebakan ile teknoloji girişimcisi Barkın Sayın, Ankara\'da düzenlenen iftar programında bir araya geldi. Program, Ramazan\'ın birlik ve dayanışma ruhunu, paylaşma kültürünü yansıtan anlamlı bir buluşma olarak değerlendirildi.',
      descriptionEn: 'Prof. Dr. Halit Yerebakan, AK Party Vice Chairman and Head of Health Policies, and technology entrepreneur Barkın Sayın came together at an iftar program in Ankara. The event was described as a meaningful gathering reflecting the spirit of unity, solidarity and sharing of Ramadan.',
      descriptionFr: 'Le Prof. Dr. Halit Yerebakan, vice-président du Parti AK et chef des politiques de santé, et l\'entrepreneur technologique Barkın Sayın se sont réunis lors d\'un programme d\'iftar à Ankara. L\'événement a été décrit comme une rencontre significative reflétant l\'esprit d\'unité, de solidarité et de partage du Ramadan.',
      descriptionEs: 'El Prof. Dr. Halit Yerebakan, vicepresidente del Partido AK y jefe de políticas de salud, y el empresario tecnológico Barkın Sayın se reunieron en un programa de iftar en Ankara. El evento fue descrito como un encuentro significativo que refleja el espíritu de unidad, solidaridad y compartir del Ramadán.',
      descriptionAr: 'اجتمع البروفيسور د. خليط يريباكان، نائب رئيس حزب العدالة والتنمية ورئيس سياسات الصحة، ورجل الأعمال التكنولوجي باركين صايين في برنامج إفطار في أنقرة. تم وصف الحدث بأنه لقاء ذو مغزى يعكس روح الوحدة والتضامن والمشاركة في رمضان.'
    },
    {
      id: 'halit-yerebakan-iftar-ankara',
      title: 'TABA-AmCham Üyeleri Barkın Sayın ve Serkan Bayram TBMM\'de Bir Araya Geldi',
      titleEn: 'TABA-AmCham Members Barkın Sayın and Serkan Bayram Meet at TBMM',
      titleFr: 'Les membres de TABA-AmCham Barkın Sayın et Serkan Bayram se rencontrent à la TBMM',
      titleEs: 'Los miembros de TABA-AmCham Barkın Sayın y Serkan Bayram se reúnen en la TBMM',
      titleAr: 'أعضاء TABA-AmCham باركين صايين وسركان بيرام يلتقيان في البرلمان التركي',
      image: '/images/press/iftar-halit-yerebakan.webp',
      source: 'Ankara / TBMM',
      category: 'events',
      date: '2025-03-02',
      description: 'TABA-AmCham üyesi iş insanı Barkın Sayın ile TABA-AmCham Yüksek Danışma Kurulu üyesi Av. Serkan Bayram, TBMM\'de bir araya geldi. Toplantıda iş dünyası ile kamu arasındaki diyaloğun ve Türkiye–ABD iş birliğinin önemi vurgulandı.',
      descriptionEn: 'TABA-AmCham member Barkın Sayın and TABA-AmCham High Advisory Board member Serkan Bayram met at the Grand National Assembly of Turkey (TBMM). The meeting underlined the importance of dialogue between the business world and the public and of Turkey–US cooperation.',
      descriptionFr: 'Le membre de TABA-AmCham Barkın Sayın et le membre du Conseil consultatif supérieur de TABA-AmCham Serkan Bayram se sont rencontrés à la Grande Assemblée nationale de Turquie (TBMM). La réunion a souligné l\'importance du dialogue entre le monde des affaires et le secteur public et de la coopération Turquie-États-Unis.',
      descriptionEs: 'El miembro de TABA-AmCham Barkın Sayın y el miembro del Consejo Asesor Superior de TABA-AmCham Serkan Bayram se reunieron en la Gran Asamblea Nacional de Turquía (TBMM). La reunión subrayó la importancia del diálogo entre el mundo empresarial y el sector público y de la cooperación Turquía-EE.UU.',
      descriptionAr: 'التقى عضو TABA-AmCham باركين صايين وعضو المجلس الاستشاري العالي لـ TABA-AmCham سركان بيرام في البرلمان التركي الكبير (TBMM). أكد الاجتماع على أهمية الحوار بين عالم الأعمال والقطاع العام والتعاون بين تركيا والولايات المتحدة.'
    },
    {
      id: 'bss-barkin-hulusi-akar-tbmm',
      title: 'BSS Holding Başkanı Barkın Sayın, Eski Milli Savunma Bakanı Hulusi Akar ile Gazi Meclis\'te Görüştü',
      titleEn: 'BSS Holding Chairman Barkın Sayın Meets Former Defense Minister Hulusi Akar at Grand National Assembly',
      titleFr: 'Le président de BSS Holding Barkın Sayın rencontre l\'ancien ministre de la Défense Hulusi Akar à la Grande Assemblée nationale',
      titleEs: 'El presidente de BSS Holding Barkın Sayın se reúne con el ex ministro de Defensa Hulusi Akar en la Gran Asamblea Nacional',
      titleAr: 'رئيس BSS Holding باركين صايين يلتقي وزير الدفاع السابق خلوصي أكار في البرلمان التركي الكبير',
      image: '/images/press/bss-hulusi-akar-tbmm.webp',
      source: 'Ankara / TBMM',
      category: 'events',
      date: '2025-02-28',
      description: 'Sayın, Akar\'ın ülkeye verdiği hizmetlere duyduğu saygıyı vurgulayarak başarılarının devamını diledi. Görüşme, Gazi Meclis\'te gerçekleşen anlamlı bir temas olarak değerlendirildi.',
      descriptionEn: 'Sayın emphasized his respect for Akar\'s services to the country and wished him continued success. The meeting was evaluated as a significant contact held at the Grand National Assembly.',
      descriptionFr: 'Sayın a souligné son respect pour les services d\'Akar au pays et lui a souhaité un succès continu. La réunion a été évaluée comme un contact important tenu à la Grande Assemblée nationale.',
      descriptionEs: 'Sayın enfatizó su respeto por los servicios de Akar al país y le deseó un éxito continuo. La reunión fue evaluada como un contacto significativo celebrado en la Gran Asamblea Nacional.',
      descriptionAr: 'أكد صايين احترامه لخدمات أكار للبلاد وتمنى له النجاح المستمر. تم تقييم الاجتماع كاتصال مهم عقد في البرلمان التركي الكبير.'
    },
    {
      id: 'saglik-bakani-iftar-ankara',
      title: 'Sağlık Bakanımız Prof. Dr. Kemal Memişoğlu ile Ramazan\'ın İlk İftarında Ankara\'da Buluşma',
      titleEn: 'Meeting with Health Minister Prof. Dr. Kemal Memişoğlu at First Iftar of Ramadan in Ankara',
      titleFr: 'Rencontre avec le ministre de la Santé Prof. Dr. Kemal Memişoğlu au premier iftar du Ramadan à Ankara',
      titleEs: 'Reunión con el Ministro de Salud Prof. Dr. Kemal Memişoğlu en el primer iftar del Ramadán en Ankara',
      titleAr: 'لقاء مع وزير الصحة البروفيسور د. كمال ميميشوغلو في أول إفطار رمضان في أنقرة',
      image: '/images/press/saglik-bakani-iftar-ankara.webp',
      source: 'Ankara — Ramazan\'ın 1. Günü',
      category: 'events',
      date: '2025-03-01',
      description: 'Ramazan\'ın 1. gününde Ankara\'da düzenlenen iftar programında bir araya gelindi.',
      descriptionEn: 'A meeting at the iftar program held in Ankara on the first day of Ramadan.',
      descriptionFr: 'Une réunion lors du programme d\'iftar organisé à Ankara le premier jour du Ramadan.',
      descriptionEs: 'Una reunión en el programa de iftar celebrado en Ankara el primer día del Ramadán.',
      descriptionAr: 'اجتماع في برنامج الإفطار المقام في أنقرة في اليوم الأول من رمضان.'
    },
    {
      id: 'vizyoner-25-konnektivite',
      title: 'VİZYONER \'25 | Konnektivite, sermaye ve yeni oyun alanları',
      titleEn: 'VISIONARY \'25 | Connectivity, Capital and New Playing Fields',
      titleFr: 'VISIONNAIRE \'25 | Connectivité, capital et nouveaux terrains de jeu',
      titleEs: 'VISIONARIO \'25 | Conectividad, capital y nuevos campos de juego',
      titleAr: 'رؤيوي \'25 | الاتصال ورأس المال وساحات اللعب الجديدة',
      image: '/images/press/vizyoner25-1.webp',
      images: [
        '/images/press/vizyoner25-1.webp',
        '/images/press/vizyoner25-2.webp',
        '/images/press/vizyoner25-3.webp',
        '/images/press/vizyoner25-4.webp'
      ],
      source: 'MÜSİAD Vizyoner\'25',
      category: 'events',
      date: '2025-12-02',
      description: '2 Aralık 2025, Atatürk Kültür Merkezi\'nde düzenlenen Vizyoner\'25 "Konnektivite, sermaye ve yeni oyun alanları" etkinliğinde yer aldık. İş dünyası liderleriyle network, teknoloji ve sermaye başlıklarında bağlantı kurduk, küresel iş birliği ve yeni fırsat alanlarını değerlendirdik.',
      descriptionEn: 'We took part in Vizyoner\'25 "Connectivity, capital and new playing fields" at Atatürk Cultural Center on 2 December 2025. We connected with business leaders on network, technology and capital, and explored global cooperation and new opportunities.',
      descriptionFr: 'Nous avons participé à Vizyoner\'25 "Connectivité, capital et nouveaux terrains de jeu" au Centre culturel Atatürk le 2 décembre 2025. Nous nous sommes connectés avec des chefs d\'entreprise sur les réseaux, la technologie et le capital, et avons exploré la coopération mondiale et de nouvelles opportunités.',
      descriptionEs: 'Participamos en Vizyoner\'25 "Conectividad, capital y nuevos campos de juego" en el Centro Cultural Atatürk el 2 de diciembre de 2025. Nos conectamos con líderes empresariales en redes, tecnología y capital, y exploramos la cooperación global y nuevas oportunidades.',
      descriptionAr: 'شاركنا في Vizyoner\'25 "الاتصال ورأس المال وساحات اللعب الجديدة" في مركز أتاتورك الثقافي في 2 ديسمبر 2025. تواصلنا مع قادة الأعمال حول الشبكات والتكنولوجيا ورأس المال، واستكشفنا التعاون العالمي والفرص الجديدة.'
    },
    {
      id: 'taba-amcham-vizyon-toplantisi-ve-etkinlikler',
      title: 'TABA-AmCham Vizyon Toplantısı ve Etkinlikler',
      titleEn: 'TABA-AmCham Vision Meeting and Events',
      titleFr: 'Réunion de vision et événements TABA-AmCham',
      titleEs: 'Reunión de visión y eventos de TABA-AmCham',
      titleAr: 'اجتماع الرؤية والفعاليات TABA-AmCham',
      image: '/images/press/IMG_8552.webp',
      images: [
        '/images/press/IMG_8552.webp',
        '/images/press/IMG_8553.webp'
      ],
      source: 'TABA-AmCham',
      category: 'events',
      description: 'TABA-AmCham Business Club\'tan Türkiye-ABD İş Dünyasına Önemli Vizyon Toplantısı. Finansal Erişim ve Borsa Piyasasında İşlem Potansiyeli temasıyla düzenlenen toplantıda, Türkiye ve ABD arasındaki iş birliği fırsatları ve ortak projeler ele alındı. Ayrıca TABA-AmCham\'ın Bölgesel İş Birliği ve Yatırım Buluşması ile Otomotivin Dönüşümü Konferansı\'na katılım gerçekleştirildi.',
      descriptionEn: 'Key Turkey–US Vision Meeting from TABA-AmCham Business Club. The meeting on Financial Access and Stock Market Potential addressed cooperation opportunities and joint projects between Turkey and the US, and participation in TABA-AmCham Regional Cooperation and Investment Meeting and Automotive Transformation Conference.',
      descriptionFr: 'Réunion de vision clé Turquie-États-Unis du TABA-AmCham Business Club. La réunion sur l\'accès financier et le potentiel du marché boursier a abordé les opportunités de coopération et les projets communs entre la Turquie et les États-Unis.',
      descriptionEs: 'Reunión de visión clave Turquía-EE.UU. del TABA-AmCham Business Club. La reunión sobre acceso financiero y potencial del mercado bursátil abordó oportunidades de cooperación y proyectos conjuntos entre Turquía y EE.UU.',
      descriptionAr: 'اجتماع رؤية رئيسي بين تركيا والولايات المتحدة من TABA-AmCham Business Club. تناول الاجتماع حول الوصول المالي وإمكانات سوق الأسهم فرص التعاون والمشاريع المشتركة بين تركيا والولايات المتحدة.'
    },
    {
      id: 'dunya-kuresel-ekonomi-zirvesi-2025',
      title: 'WORLD Küresel Ekonomi Zirvesi 2025',
      titleEn: 'WORLD Global Economy Summit 2025',
      titleFr: 'MONDE Sommet de l\'économie mondiale 2025',
      titleEs: 'MUNDO Cumbre de Economía Global 2025',
      titleAr: 'العالم قمة الاقتصاد العالمي 2025',
      image: '/images/press/IMG_8106.webp',
      images: [
        '/images/press/IMG_8106.webp',
        '/images/press/IMG_8107.webp',
        '/images/press/IMG_8138.webp',
        '/images/press/IMG_8168.webp'
      ],
      source: 'DÜNYA Gazetesi',
      category: 'events',
      description: 'İstanbul\'da düzenlenen DÜNYA Küresel Ekonomi Zirvesi 2025\'e katıldım. Hazine ve Maliye Bakanı\'nın konuşmasını dinledik; Av. Serkan Bayram, TABA-AmCham ailesi ve banka üst yöneticileriyle bir araya geldik. 2025–2026 görünümü üzerine verimli görüşmeler yaptık. Teşekkürler DÜNYA ailesi.',
      descriptionEn: 'I attended the DÜNYA Global Economy Summit 2025 in Istanbul. We listened to the Minister of Treasury and Finance; met with Av. Serkan Bayram, the TABA-AmCham family and senior bank executives. Fruitful discussions on the 2025–2026 outlook. Thanks to the DÜNYA family.',
      descriptionFr: 'J\'ai assisté au Sommet de l\'économie mondiale DÜNYA 2025 à Istanbul. Nous avons écouté le ministre du Trésor et des Finances ; rencontré Av. Serkan Bayram, la famille TABA-AmCham et les cadres supérieurs des banques. Discussions fructueuses sur les perspectives 2025-2026.',
      descriptionEs: 'Asistí a la Cumbre de Economía Global DÜNYA 2025 en Estambul. Escuchamos al Ministro de Hacienda y Finanzas; nos reunimos con Av. Serkan Bayram, la familia TABA-AmCham y altos ejecutivos bancarios. Discusiones fructíferas sobre las perspectivas 2025-2026.',
      descriptionAr: 'حضرت قمة الاقتصاد العالمي DÜNYA 2025 في إسطنبول. استمعنا إلى وزير الخزانة والمالية؛ التقينا بـ Av. Serkan Bayram وعائلة TABA-AmCham وكبار المسؤولين التنفيذيين في البنوك. مناقشات مثمرة حول توقعات 2025-2026.'
    },
    {
      id: 'taba-amcham-trade-on-tv-magazine',
      title: 'Trade on TV, TABA-AmCham dergisinde ABD\'de yer aldı',
      titleEn: 'Trade on TV Featured in TABA-AmCham Magazine in the USA',
      titleFr: 'Trade on TV présenté dans le magazine TABA-AmCham aux États-Unis',
      titleEs: 'Trade on TV destacado en la revista TABA-AmCham en EE.UU.',
      titleAr: 'Trade on TV يظهر في مجلة TABA-AmCham في الولايات المتحدة',
      image: '/images/press/IMG_8023.webp',
      source: 'TABA-AmCham',
      category: 'news',
      description: 'Finansın akıllı TV\'deki öncüsü, ABD pazarındaki görünürlüğünü güçlendiriyor. Trade on TV platformu, TABA-AmCham dergisinde yer alarak Amerika Birleşik Devletleri\'ndeki finansal teknoloji ekosisteminde önemli bir konum elde etti.',
      descriptionEn: 'The pioneer of finance on smart TV is strengthening its presence in the US market. The Trade on TV platform has secured an important position in the US fintech ecosystem by featuring in the TABA-AmCham magazine.',
      descriptionFr: 'Le pionnier de la finance sur la télévision intelligente renforce sa présence sur le marché américain. La plateforme Trade on TV a obtenu une position importante dans l\'écosystème fintech américain en figurant dans le magazine TABA-AmCham.',
      descriptionEs: 'El pionero de las finanzas en televisión inteligente está fortaleciendo su presencia en el mercado estadounidense. La plataforma Trade on TV ha asegurado una posición importante en el ecosistema fintech de EE.UU. al aparecer en la revista TABA-AmCham.',
      descriptionAr: 'رائد التمويل على التلفزيون الذكي يعزز وجوده في السوق الأمريكية. حصلت منصة Trade on TV على مكانة مهمة في النظام البيئي للتكنولوجيا المالية الأمريكية من خلال ظهورها في مجلة TABA-AmCham.'
    },
    {
      id: 'fortune500-turkiye-odul-toreni-2025',
      title: 'Fortune 500 Türkiye Ödül Töreni 2025',
      titleEn: 'Fortune 500 Turkey Awards Ceremony 2025',
      titleFr: 'Cérémonie de remise des prix Fortune 500 Turquie 2025',
      titleEs: 'Ceremonia de premios Fortune 500 Turquía 2025',
      titleAr: 'حفل توزيع جوائز Fortune 500 تركيا 2025',
      image: '/images/press/fortune500-6.webp',
      images: [
        '/images/press/fortune500-6.webp'
      ],
      source: 'Fortune 500 Türkiye',
      category: 'events',
      description: 'Koray Bey\'in davetiyle katıldığım Fortune 500 Türkiye Ödül Töreni\'nde, ülkemizin en güçlü markalarıyla aynı salonda olmak gurur vericiydi. İlham veren bir geceydi.',
      descriptionEn: 'It was an honour to be in the same room with Turkey’s strongest brands at the Fortune 500 Turkey Awards 2025, which I attended at the invitation of Koray Bey. An inspiring evening.',
      descriptionFr: 'C\'était un honneur d\'être dans la même salle que les marques les plus fortes de Turquie lors de la cérémonie des prix Fortune 500 Turquie 2025, à laquelle j\'ai assisté sur invitation de Koray Bey. Une soirée inspirante.',
      descriptionEs: 'Fue un honor estar en la misma sala con las marcas más fuertes de Turquía en la ceremonia de premios Fortune 500 Turquía 2025, a la que asistí por invitación de Koray Bey. Una velada inspiradora.',
      descriptionAr: 'كان من الشرف أن أكون في نفس القاعة مع أقوى العلامات التجارية في تركيا في حفل جوائز Fortune 500 تركيا 2025، الذي حضرته بدعوة من Koray Bey. أمسية ملهمة.'
    },
    {
      id: 'taba-amcham-2plan-istanbul-etkinligi',
      title: 'TABA-AmCham 2Plan İstanbul Etkinliği',
      titleEn: 'TABA-AmCham 2Plan Istanbul Event',
      titleFr: 'Événement TABA-AmCham 2Plan Istanbul',
      titleEs: 'Evento TABA-AmCham 2Plan Estambul',
      titleAr: 'فعالية TABA-AmCham 2Plan إسطنبول',
      image: '/images/press/fortune500-1.webp',
      images: [
        '/images/press/fortune500-1.webp',
        '/images/press/fortune500-2.webp',
        '/images/press/fortune500-3.webp'
      ],
      source: 'TABA-AmCham',
      category: 'events',
      url: 'https://www.linkedin.com/feed/update/urn:li:activity:7386459559084761088/',
      description: 'Koray Bey\'in davetiyle, TABA-AmCham ailesiyle birlikte 2Plan İstanbul etkinliğine katıldık. Otomotiv sektörünün dönüşümünü, dijitalleşmenin getirdiği yeni fırsatları ve iş dünyasındaki iş birliği potansiyellerini değerlendirdiğimiz verimli bir gün oldu. Teşekkürler TABA-AmCham Çağrı Koray ÖZTOPÇU 2plan Otomotiv GarantiBBVA',
      descriptionEn: 'At the invitation of Koray Bey, we joined the TABA-AmCham family at the 2Plan Istanbul event. A productive day evaluating the transformation of the automotive sector, new opportunities from digitalisation and cooperation potential in the business world.',
      descriptionFr: 'Sur invitation de Koray Bey, nous avons rejoint la famille TABA-AmCham à l\'événement 2Plan Istanbul. Une journée productive évaluant la transformation du secteur automobile, les nouvelles opportunités de la numérisation et le potentiel de coopération dans le monde des affaires.',
      descriptionEs: 'Por invitación de Koray Bey, nos unimos a la familia TABA-AmCham en el evento 2Plan Estambul. Un día productivo evaluando la transformación del sector automotriz, nuevas oportunidades de la digitalización y potencial de cooperación en el mundo empresarial.',
      descriptionAr: 'بدعوة من Koray Bey، انضممنا إلى عائلة TABA-AmCham في فعالية 2Plan إسطنبول. يوم منتج لتقييم تحول قطاع السيارات والفرص الجديدة من الرقمنة وإمكانات التعاون في عالم الأعمال.'
    },
    {
      id: 'business-trip-istanbul',
      title: 'İş Seyahati | İstanbul',
      titleEn: 'Business Trip | Istanbul',
      titleFr: 'Voyage d’affaires | Istanbul',
      titleEs: 'Viaje de negocios | Estambul',
      titleAr: 'رحلة عمل | إسطنبول',
      image: '/images/press/fortune500-4.webp',
      images: [
        '/images/press/fortune500-4.webp',
        '/images/press/fortune500-5.webp',
        '/images/press/fortune500-7.webp',
        '/images/press/fortune500-8.webp'
      ],
      source: 'Business Trip',
      category: 'events',
      url: 'https://www.linkedin.com/feed/update/urn:li:activity:7387132985877315584/',
      description: 'Bu hafta İstanbul\'da üst düzey iş toplantıları ve etkinliklere katıldım. TABA-AmCham Genel Merkezi\'nde Türkiye, ABD ve Körfez bölgesi arasında yeni ortaklık fırsatlarına odaklanan bir yatırım toplantısı gerçekleştirdik. Ardından Fortune 500 Türkiye Ödülleri 2025\'e katılarak önde gelen yöneticiler ve vizyoner girişimcilerle bir araya geldim. Strateji, inovasyon ve değerli bağlantılarla dolu verimli bir seyahat oldu.',
      descriptionEn: 'This week in Istanbul, I attended several high-level business meetings and events. At the TABA-AmCham Headquarters, we held an investment meeting focused on new partnership opportunities between Türkiye, the U.S., and the Gulf region. Later, I also joined the Fortune 500 Türkiye Awards 2025, where I had the chance to connect with leading executives and visionary entrepreneurs. A productive trip full of strategy, innovation, and valuable connections.',
      descriptionFr: 'Cette semaine à Istanbul, j\'ai assisté à plusieurs réunions d\'affaires et événements de haut niveau. Au siège de TABA-AmCham, nous avons tenu une réunion d\'investissement axée sur de nouvelles opportunités de partenariat entre la Turquie, les États-Unis et la région du Golfe. Plus tard, j\'ai également rejoint les Fortune 500 Türkiye Awards 2025, où j\'ai eu l\'occasion de me connecter avec des cadres de premier plan et des entrepreneurs visionnaires.',
      descriptionEs: 'Esta semana en Estambul, asistí a varias reuniones de negocios y eventos de alto nivel. En la sede de TABA-AmCham, realizamos una reunión de inversión centrada en nuevas oportunidades de asociación entre Turquía, EE.UU. y la región del Golfo. Más tarde, también me uní a los Fortune 500 Türkiye Awards 2025, donde tuve la oportunidad de conectar con ejecutivos líderes y empresarios visionarios.',
      descriptionAr: 'هذا الأسبوع في إسطنبول، حضرت العديد من اجتماعات الأعمال والفعاليات رفيعة المستوى. في مقر TABA-AmCham، عقدنا اجتماع استثمار يركز على فرص الشراكة الجديدة بين تركيا والولايات المتحدة ومنطقة الخليج. في وقت لاحق، انضممت أيضًا إلى جوائز Fortune 500 تركيا 2025، حيث أتيحت لي الفرصة للتواصل مع كبار المديرين التنفيذيين ورجال الأعمال أصحاب الرؤى.'
    },
    {
      id: 'bosco-conference-istanbul-2025',
      title: '🌍 Bosco Conference Istanbul 2025\'te Temsil',
      titleEn: '🌍 Represented at Bosco Conference Istanbul 2025',
      titleFr: '🌍 Représenté à la Conférence Bosco Istanbul 2025',
      titleEs: '🌍 Representado en la Conferencia Bosco Estambul 2025',
      titleAr: '🌍 ممثل في مؤتمر Bosco إسطنبول 2025',
      image: '/images/press/IMG_7456.webp',
      images: [
        '/images/press/IMG_7456.webp',
        '/images/press/IMG_7454.webp',
        '/images/press/IMG_7451.webp',
        '/images/press/IMG_7452.webp',
        '/images/press/IMG_7453.webp',
        '/images/press/IMG_7458.webp'
      ],
      source: 'Bosco Conference',
      category: 'events',
      url: 'https://www.linkedin.com/posts/barkinsayin_represented-at-bosco-conference-istanbul-activity-7381385246644195328-Zl1Q',
      description: 'Bosco Conference 2025 için İstanbul\'da geçen verimli 24 saat — dünyanın dört bir yanından yatırımcılar, varlık yöneticileri ve inovasyoncularla buluştum. AllSoftware24\'ü temsil etmek ve Trade on TV\'nin Akıllı TV finans deneyimini nasıl yeniden tanımladığını paylaşmak gurur vericiydi.',
      descriptionEn: 'A productive 24 hours in Istanbul for the Bosco Conference 2025 — connecting with investors, wealth managers, and innovators from around the world. Proud to represent AllSoftware24 and share how Trade on TV is redefining the Smart-TV financial experience.',
      descriptionFr: '24 heures productives à Istanbul pour la Conférence Bosco 2025 — en contact avec des investisseurs, des gestionnaires de patrimoine et des innovateurs du monde entier. Fier de représenter AllSoftware24 et de partager comment Trade on TV redéfinit l\'expérience financière de la télévision intelligente.',
      descriptionEs: '24 horas productivas en Estambul para la Conferencia Bosco 2025 — conectando con inversores, gestores de patrimonio e innovadores de todo el mundo. Orgulloso de representar a AllSoftware24 y compartir cómo Trade on TV está redefiniendo la experiencia financiera de la televisión inteligente.',
      descriptionAr: '24 ساعة منتجة في إسطنبول لمؤتمر Bosco 2025 — التواصل مع المستثمرين ومديري الثروات والمبتكرين من جميع أنحاء العالم. فخور بتمثيل AllSoftware24 ومشاركة كيف تعيد Trade on TV تعريف تجربة التمويل على التلفزيون الذكي.'
    },
    {
      id: 'turk-girisimci-iddiali-aplikasyon',
      title: 'Türk girişimciden iddialı aplikasyon Trade on TV',
      titleEn: 'Ambitious Application Trade on TV from Turkish Entrepreneur',
      titleFr: 'Application ambitieuse Trade on TV d’un entrepreneur turc',
      titleEs: 'Aplicación ambiciosa Trade on TV de un empresario turco',
      titleAr: 'تطبيق طموح Trade on TV من رجل أعمال تركي',
      image: '/images/press/IMG_7432.webp',
      source: 'Yeni Asır',
      category: 'news',
      url: 'https://www.yeniasir.com.tr/ekonomi/2025/10/07/turk-girisimciden-iddiali-aplikasyon',
      description: 'Çalışmalarını yurt dışında sürdüren Türk girişimci Barkın Sayın, finans içeriklerini televizyon ekranına taşıyan \'Trade on TV\' platformunu hayata geçirdi. Yapay zeka destekli analizlerin de yer aldığı uygulama, finans haberleri ve verilerini ilk kez TV\'de tek bir ekranda buluşturabilecek şekilde tasarlandı. AllSoftware24 L.L.C çatısı altında geliştirilen platform, kullanıcıların küresel piyasaları sadece TV kumandasıyla kolayca takip etmesini sağlayacak.',
      descriptionEn: 'Turkish entrepreneur Barkın Sayın, who runs his ventures abroad, has launched the Trade on TV platform, bringing financial content to the television screen. The app, which also includes AI-powered analysis, is designed to bring financial news and data together on a single TV screen for the first time. Developed under AllSoftware24 L.L.C, the platform will let users easily follow global markets with just the TV remote.',
      descriptionFr: 'L\'entrepreneur turc Barkın Sayın, qui gère ses entreprises à l\'étranger, a lancé la plateforme Trade on TV, apportant du contenu financier à l\'écran de télévision. L\'application, qui comprend également des analyses alimentées par l\'IA, est conçue pour rassembler les actualités et les données financières sur un seul écran de télévision pour la première fois. Développée sous AllSoftware24 L.L.C, la plateforme permettra aux utilisateurs de suivre facilement les marchés mondiaux avec juste la télécommande.',
      descriptionEs: 'El empresario turco Barkın Sayın, que dirige sus empresas en el extranjero, ha lanzado la plataforma Trade on TV, llevando contenido financiero a la pantalla del televisor. La aplicación, que también incluye análisis impulsados por IA, está diseñada para reunir noticias y datos financieros en una sola pantalla de TV por primera vez. Desarrollada bajo AllSoftware24 L.L.C, la plataforma permitirá a los usuarios seguir fácilmente los mercados globales solo con el control remoto del TV.',
      descriptionAr: 'أطلق رجل الأعمال التركي باركين صايين، الذي يدير مشاريعه في الخارج، منصة Trade on TV، التي تجلب المحتوى المالي إلى شاشة التلفزيون. التطبيق، الذي يتضمن أيضًا تحليلات مدعومة بالذكاء الاصطناعي، مصمم لجمع الأخبار والبيانات المالية على شاشة تلفزيون واحدة لأول مرة. تم تطويرها تحت AllSoftware24 L.L.C، ستتيح المنصة للمستخدمين متابعة الأسواق العالمية بسهولة باستخدام جهاز التحكم عن بُعد فقط.'
    },
    {
      id: 'mwc',
      title: 'MWC - Barcelona 2025',
      image: '/images/press/mwc.avif',
      source: 'Mobile World Congress',
      category: 'events',
      url: 'https://www.linkedin.com/posts/barkinsayin_mwc2025-innovation-technology-activity-7307053538361118720-Txs0'
    },
    {
      id: 'fintech',
      title: 'Fintech - FiBe 2025',
      image: '/images/press/fintech.avif',
      source: 'Fintech Berlin',
      category: 'events',
      url: 'https://www.linkedin.com/posts/barkinsayin_fibe2025-berlin-fintech-activity-7316852108832481280--i1b'
    },
    {
      id: 'meeting-finance-leaders',
      title: 'Finans ve İş Dünyası Liderleriyle Buluşma',
      titleEn: 'Meeting with Leaders of the Finance and Business World',
      titleFr: 'Rencontre avec les leaders de la finance et du monde des affaires',
      titleEs: 'Reunión con líderes de las finanzas y el mundo empresarial',
      titleAr: 'لقاء مع قادة المالية وعالم الأعمال',
      image: '/images/press/IMG_7241.webp',
      source: 'Event',
      category: 'events',
      url: 'https://www.linkedin.com/posts/barkinsayin_it-was-a-privilege-to-meet-with-leading-figures-activity-7378436925805858817-JLJX',
      description: 'Finans ve iş dünyasının önde gelen isimleriyle buluşup değerli görüş alışverişinde bulunmaktan mutluluk duyduk. Etkinlikte TABA-AmCham ekibi ve Çağrı Koray Öztopçu (Garanti BBVA, Bireysel Finans Direktörü) ile bir araya geldik. Geleceği şekillendirmede asıl güç, bu tür platformlarda kurulan ortaklıklar ve iş birliklerinde yatıyor.',
      descriptionEn: 'We were delighted to meet with leading figures from the finance and business world and exchange valuable insights. During the event, we connected with the TABA-AmCham team and Çağrı Koray Öztopçu (Garanti BBVA, Consumer Finance Director). The true strength in shaping the future lies in partnerships and collaborations built on platforms like these.',
      descriptionFr: 'Nous avons été ravis de rencontrer des personnalités de premier plan du monde de la finance et des affaires et d\'échanger des idées précieuses. Lors de l\'événement, nous nous sommes connectés avec l\'équipe TABA-AmCham et Çağrı Koray Öztopçu (Garanti BBVA, Directeur des finances personnelles). La véritable force pour façonner l\'avenir réside dans les partenariats et les collaborations construits sur des plateformes comme celles-ci.',
      descriptionEs: 'Nos encantó reunirnos con figuras destacadas del mundo de las finanzas y los negocios e intercambiar ideas valiosas. Durante el evento, nos conectamos con el equipo TABA-AmCham y Çağrı Koray Öztopçu (Garanti BBVA, Director de Finanzas al Consumidor). La verdadera fuerza para dar forma al futuro radica en las asociaciones y colaboraciones construidas en plataformas como estas.',
      descriptionAr: 'كنا سعداء بلقاء شخصيات بارزة من عالم المال والأعمال وتبادل الأفكار القيمة. خلال الحدث، تواصلنا مع فريق TABA-AmCham وÇağrı Koray Öztopçu (Garanti BBVA، مدير التمويل الاستهلاكي). تكمن القوة الحقيقية في تشكيل المستقبل في الشراكات والتعاون المبني على منصات مثل هذه.'
    },
    {
      id: 'hurriyet',
      title: 'HÜRRİYET - Avrupa ile dostane ilişkiler artmalı',
      titleEn: 'HÜRRIYET - Friendly relations with Europe should increase',
      titleFr: 'HÜRRIYET - Les relations amicales avec l\'Europe doivent augmenter',
      titleEs: 'HÜRRIYET - Las relaciones amistosas con Europa deben aumentar',
      titleAr: 'HÜRRIYET - يجب زيادة العلاقات الودية مع أوروبا',
      image: '/images/press/hurriyet.avif',
      source: 'Hürriyet',
      category: 'news'
    },
    {
      id: 'milliyet',
      title: 'MİLLİYET - 192 ülkede ilk 10\'da',
      titleEn: 'MILLIYET - Top 10 in 192 countries',
      titleFr: 'MILLIYET - Top 10 dans 192 pays',
      titleEs: 'MILLIYET - Top 10 en 192 países',
      titleAr: 'MILLIYET - أفضل 10 في 192 دولة',
      image: '/images/press/milliyet.avif',
      source: 'Milliyet',
      category: 'news'
    },
    {
      id: 'ege',
      title: 'Ege Telegraf',
      image: '/images/press/ege-telegraf.avif',
      source: 'Ege Telegraf',
      category: 'interviews'
    },
    {
      id: 'posta',
      title: 'POSTA - CRYPTO\'da yeni dönem',
      titleEn: 'POSTA - New era in CRYPTO',
      titleFr: 'POSTA - Nouvelle ère dans la CRYPTO',
      titleEs: 'POSTA - Nueva era en CRYPTO',
      titleAr: 'POSTA - عصر جديد في العملات المشفرة',
      image: '/images/press/posta.avif',
      source: 'Posta',
      category: 'news'
    }
  ];
  
  // Uzun format haberler (9:16)
  const tallFormatMedia: MediaItem[] = [
    {
      id: 'taba-amcham',
      title: 'TABA-AmCham Üyeler Arası Network Buluşması ve Stratejik İşbirliği İmza Töreni',
      titleEn: 'TABA-AmCham Members Network Meeting and Strategic Cooperation Signing Ceremony',
      titleFr: 'Réunion de réseau des membres TABA-AmCham et cérémonie de signature de coopération stratégique',
      titleEs: 'Reunión de red de miembros de TABA-AmCham y ceremonia de firma de cooperación estratégica',
      titleAr: 'اجتماع شبكة أعضاء TABA-AmCham وحفل توقيع التعاون الاستراتيجي',
      image: '/images/press/taba-amcham.webp',
      source: 'TABA-AmCham',
      category: 'events',
      description: 'TABA-AmCham üyeleri arasında gerçekleştirilen network buluşması ve stratejik işbirliği imza töreni. Bu etkinlik, sektördeki önemli işbirliklerinin temelini attı ve gelecekteki projeler için güçlü bir altyapı oluşturdu.',
      descriptionEn: 'Network meeting and strategic cooperation signing ceremony among TABA-AmCham members. The event laid the groundwork for key collaborations in the sector and built a strong foundation for future projects.',
      descriptionFr: 'Réunion de réseau et cérémonie de signature de coopération stratégique parmi les membres de TABA-AmCham. L\'événement a jeté les bases de collaborations clés dans le secteur et a construit une base solide pour les projets futurs.',
      descriptionEs: 'Reunión de red y ceremonia de firma de cooperación estratégica entre los miembros de TABA-AmCham. El evento sentó las bases para colaboraciones clave en el sector y construyó una base sólida para proyectos futuros.',
      descriptionAr: 'اجتماع شبكة وحفل توقيع التعاون الاستراتيجي بين أعضاء TABA-AmCham. وضع الحدث الأساس للتعاون الرئيسي في القطاع وبنى أساسًا قويًا للمشاريع المستقبلية.'
    },
    {
      id: 'izmirin',
      title: 'İzmir\'in Habercisi - 192 Ülkede Zirveye Tırmandı',
      titleEn: 'İzmir\'in Habercisi - Climbed to the Top in 192 Countries',
      titleFr: 'İzmir\'in Habercisi - Grimpé au sommet dans 192 pays',
      titleEs: 'İzmir\'in Habercisi - Escaló a la cima en 192 países',
      titleAr: 'İzmir\'in Habercisi - تسلق إلى القمة في 192 دولة',
      image: '/images/press/izmirin-habercisi.webp',
      source: 'İzmir\'in Habercisi',
      category: 'interviews',
      url: 'https://www.izmirinhabercisi.com/192-ulkede-zirveye-tirmandi'
    },
    {
      id: 'haberini',
      title: 'Haberini Oku - 192 Ülkede Zirveye Tırmandı',
      titleEn: 'Haberini Oku - Climbed to the Top in 192 Countries',
      titleFr: 'Haberini Oku - Grimpé au sommet dans 192 pays',
      titleEs: 'Haberini Oku - Escaló a la cima en 192 países',
      titleAr: 'Haberini Oku - تسلق إلى القمة في 192 دولة',
      image: '/images/press/haberini-oku.webp',
      source: 'Haberini Oku',
      category: 'news',
      url: 'https://www.haberinioku.com/192-ulkede-zirveye-tirmandi/209577/'
    },
    {
      id: 'egemenlik',
      title: 'Egemenlik - 192 Ülkede Zirveye Tırmandı',
      titleEn: 'Egemenlik - Climbed to the Top in 192 Countries',
      titleFr: 'Egemenlik - Grimpé au sommet dans 192 pays',
      titleEs: 'Egemenlik - Escaló a la cima en 192 países',
      titleAr: 'Egemenlik - تسلق إلى القمة في 192 دولة',
      image: '/images/press/egemenlik.webp',
      source: 'Egemenlik',
      category: 'press',
      url: 'https://www.egemenlik.com.tr/192-ulkede-zirveye-tirmandi/8823/'
    },
    {
      id: 'haber-hurriyet',
      title: 'Haber Hürriyet - Türk İş İnsanı 192 Ülkede Zirveye Tırmandı',
      titleEn: 'Haber Hürriyet - Turkish Businessman Climbed to the Top in 192 Countries',
      titleFr: 'Haber Hürriyet - Un homme d\'affaires turc grimpe au sommet dans 192 pays',
      titleEs: 'Haber Hürriyet - Empresario turco escaló a la cima en 192 países',
      titleAr: 'Haber Hürriyet - رجل أعمال تركي يتسلق إلى القمة في 192 دولة',
      image: '/images/press/haber-hurriyet.webp',
      source: 'Haber Hürriyet',
      category: 'news',
      url: 'https://www.haberhurriyeti.com/haber/12576918/turk-is-insani-192-ulkede-zirveye-tirmandi'
    },
    {
      id: 'haber35',
      title: 'Haber 35 - 192 Ülkede Zirveye Tırmandı',
      titleEn: 'Haber 35 - Climbed to the Top in 192 Countries',
      titleFr: 'Haber 35 - Grimpé au sommet dans 192 pays',
      titleEs: 'Haber 35 - Escaló a la cima en 192 países',
      titleAr: 'Haber 35 - تسلق إلى القمة في 192 دولة',
      image: '/images/press/haber35.webp',
      source: 'Haber 35',
      category: 'interviews',
      url: 'https://www.haber35.com/192-ulkede-zirveye-tirmandi/1389/'
    },
    {
      id: 'macro',
      title: 'Macro Haber - 192 Ülkede Zirveye Tırmandı',
      titleEn: 'Macro Haber - Climbed to the Top in 192 Countries',
      titleFr: 'Macro Haber - Grimpé au sommet dans 192 pays',
      titleEs: 'Macro Haber - Escaló a la cima en 192 países',
      titleAr: 'Macro Haber - تسلق إلى القمة في 192 دولة',
      image: '/images/press/macro-haber.webp',
      source: 'Macro Haber',
      category: 'news',
      url: 'https://www.macrohaber.com/192-ulkede-zirveye-tirmandi/5369/'
    }
  ];

  // Tarih formatını düzenleyen yardımcı fonksiyon
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', options);
  };
  
  // Tüm haberleri göster
  const allMedia = [...wideFormatMedia, ...tallFormatMedia];

  // Slider functions
  const nextSlide = (itemId: string, images: string[]) => {
    setSliderStates(prev => {
      const currentIndex = prev[itemId] || 0;
      const nextIndex = (currentIndex + 1) % images.length;
      return {
        ...prev,
        [itemId]: nextIndex
      };
    });
  };

  const prevSlide = (itemId: string, images: string[]) => {
    setSliderStates(prev => {
      const currentIndex = prev[itemId] || 0;
      const prevIndex = (currentIndex - 1 + images.length) % images.length;
      return {
        ...prev,
        [itemId]: prevIndex
      };
    });
  };

  const goToSlide = (itemId: string, index: number) => {
    setSliderStates(prev => ({
      ...prev,
      [itemId]: index
    }));
  };

  // Calculate translateX percentage
  const getTranslateX = (currentIndex: number) => {
    return -currentIndex * 100;
  };

  // SEO metadata for Press page
  const pressSeoData = {
    en: {
      title: 'Press - Barkin Sayin | Media Coverage & News',
      description: 'View press coverage and media features about Barkin Sayin\'s fintech innovations, Trade on TV platform, and entrepreneurial initiatives across global markets.',
      keywords: [
        'Barkin Sayin press',
        'fintech media coverage',
        'Trade on TV news',
        'entrepreneurship media',
        'business innovation press',
        'financial technology news',
        'startup media features'
      ]
    },
    tr: {
      title: 'Basın - Barkın Sayın | Medya Haberleri',
      description: 'Barkın Sayın\'ın fintech yenilikleri, Trade on TV platformu ve küresel pazarlardaki girişimcilik faaliyetleri hakkında basın haberleri ve medya özelliklerini inceleyin.',
      keywords: [
        'Barkın Sayın basın',
        'fintech medya haberleri',
        'Trade on TV haberleri',
        'girişimcilik medya',
        'iş inovasyonu basın',
        'finansal teknoloji haberleri',
        'startup medya özellikleri'
      ]
    }
  };
  
  const seoTitle = pressSeoData[language as keyof typeof pressSeoData]?.title || pressSeoData.en.title;
  const seoDescription = pressSeoData[language as keyof typeof pressSeoData]?.description || pressSeoData.en.description;
  const seoKeywords = pressSeoData[language as keyof typeof pressSeoData]?.keywords || pressSeoData.en.keywords;

  // JSON-LD yapılandırılmış veri
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": allMedia.slice(0, 10).map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": item.category === 'news' ? "NewsArticle" : "Article",
        "headline": item.title,
        "description": getItemDescription(item),
        "image": item.image,
        "datePublished": item.date || new Date().toISOString(),
        "author": {
          "@type": "Person",
          "name": "Barkın Sayın"
        },
        "publisher": {
          "@type": "Organization",
          "name": item.source,
          "logo": {
            "@type": "ImageObject",
            "url": "https://barkinsayin.com/logo.webp"
          }
        },
        ...(item.url && { "url": item.url })
      }
    }))
  };

  return (
    <PressSection>
      {lightboxImage && (
        <LightboxOverlay onClick={() => setLightboxImage(null)}>
          <LightboxClose onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }} aria-label="Kapat">
            ×
          </LightboxClose>
          <LightboxImage src={lightboxImage} alt="" onClick={(e) => e.stopPropagation()} />
        </LightboxOverlay>
      )}
      <PressContainer>
        <SEO 
          title={seoTitle}
          description={seoDescription}
          keywords={seoKeywords}
          imageUrl="https://www.barkinsayin.com/images/press/og-press.webp"
        />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        <PageHeading>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {getText('pageTitle')}
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {getText('pageSubtitle')}
          </PageSubtitle>
        </PageHeading>
        
        
        <MediaGrid>
          {allMedia.map((item, index) => (
            <NewsCard
              key={item.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              href={item.url ? item.url : undefined}
              target={item.url ? "_blank" : undefined}
              rel={item.url ? "noopener noreferrer" : undefined}
              as={item.url ? "a" : "div"}
            >
              <CardImageContainer>
                {item.images && item.images.length > 1 ? (
                  <SimpleSlider>
                    <ImageClickWrapper
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLightboxImage(item.images![sliderStates[item.id] || 0]);
                      }}
                    >
                      <SliderImage 
                        src={item.images[sliderStates[item.id] || 0]} 
                        alt={`${item.title} - ${item.source} - Görsel ${(sliderStates[item.id] || 0) + 1}`}
                        title={item.title}
                        loading={index < 4 ? "eager" : "lazy"}
                      />
                    </ImageClickWrapper>
                    <SliderButton 
                      direction="left" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        prevSlide(item.id, item.images!);
                      }}
                      disabled={(sliderStates[item.id] || 0) === 0}
                    >
                      ‹
                    </SliderButton>
                    <SliderButton 
                      direction="right" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextSlide(item.id, item.images!);
                      }}
                      disabled={(sliderStates[item.id] || 0) === item.images!.length - 1}
                    >
                      ›
                    </SliderButton>
                  </SimpleSlider>
                ) : (
                  <ImageClickWrapper
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLightboxImage(item.image);
                    }}
                  >
                    <CardImg 
                      src={item.image} 
                      alt={`${item.title} - ${item.source}`}
                      title={item.title}
                      loading={index < 4 ? "eager" : "lazy"} 
                    />
                  </ImageClickWrapper>
                )}
              </CardImageContainer>
              <CardContent>
                {item.date && <CardDate dateTime={item.date}>{formatDate(item.date)}</CardDate>}
                <CardTitle>{getItemTitle(item)}</CardTitle>
                <CardSource>{item.source}</CardSource>
                {getItemDescription(item) ? <CardDescription>{getItemDescription(item)}</CardDescription> : null}
              </CardContent>
            </NewsCard>
          ))}
        </MediaGrid>
      </PressContainer>
    </PressSection>
  );
};

export default Press; 
